import {
  useRef, useEffect, useState, useContext,
} from 'react';
import { useParams } from 'react-router-dom';

// components
import PublicHeader from '../../components/PublicPage/PublicHeader/PublicHeader';
import PublicKpiToolbar from '../../components/PublicPage/PublicKpiToolbar/PublicKpiToolbar';
import PublicGridKpi from '../../components/PublicPage/PublicGridKpi';
import PublicBlocker from '../../components/PublicPage/PublicBlocker';
import PublicPassword from '../../components/Modals/PublicPassword';
import InfoBannerContainer from '../../components/PublicPage/PublicInfoBanner';
// import PublicFooter from '../../juristec-ui/metricsComponents/PublicPage/PublicFooter';
import Loader from '../../juristec-ui/core/Loader';

// others
import useGridKpi from '../../hooks/useGridKpi';
import useRecaptchaV3 from '../../hooks/useRecaptchaV3';
import { AlertContext } from '../../context/AlertProvider';
import { ModalContext } from '../../context/ModalProvider';

import urlHash from '../../utils/functions/urlHash';
import useLocalStorage from '../../hooks/useLocalStorage';
import DashGridSize from '../../components/Modals/DashGridSize';

const rglCols = {
  lg: 48,
  sm: 12,
  xxs: 4,
};

const getParams = (h) => {
  const str = urlHash(h, true).split('/');
  return { dashboardKey: str[0], userId: str[1] };
};

const PublicPage = () => {
  const recaptcha = useRecaptchaV3();
  const isMounted = useRef(false);
  const { hash } = useParams();
  const { dashboardKey, userId } = getParams(hash);

  const { setModalConfig, closeModal } = useContext(ModalContext);
  const { setAlertConfig } = useContext(AlertContext);

  const [reasonBlocker, setReasonBlocker] = useState(null);

  const [showInfo, setShowInfo] = useLocalStorage(`${dashboardKey}-public-show-info`, true);

  const [gridSizeLocal, setGridSizeLocal] = useLocalStorage(`${dashboardKey}-public-size`, null);
  const [gridSize, setGridSize] = useState(null);

  const [gridKpiState, gridKpiAPI] = useGridKpi(userId, dashboardKey);
  const {
    dashboardDoc, kpiItemList, kpisLayout, isLoading,
  } = gridKpiState;

  const [gridBreakpoint, setGridBreakpoint] = useState('lg');
  const [markers, setMarkers] = useState([]);

  useEffect(() => {
    const mkrs = kpiItemList.filter((kpi) => (
      ['label', 'smartLabel', 'Content'].includes(kpi.type) && kpi.style?.UseAsMarker
    )).map((insight) => {
      const layoutItem = kpisLayout?.[gridBreakpoint]?.find((it) => it.i === insight.id);
      return {
        name: insight.name || '(sem nome)',
        key: insight.id,
        x: layoutItem.x ?? 0,
        y: layoutItem.y ?? 1000,
      };
    });
    const qtdCols = rglCols[gridBreakpoint];
    setMarkers(mkrs.sort((a, b) => (a.y * qtdCols + a.x >= b.y * qtdCols + b.x ? 1 : -1)));
  }, [kpiItemList, gridBreakpoint, kpisLayout]);

  const handlePasswordModal = (submitHandler) => {
    setModalConfig({
      title: 'Informe a senha',
      children: (
        <PublicPassword
          submit={submitHandler}
        />
      ),
    });
  };

  const loadPage = async (password = null) => {
    const gToken = await recaptcha.getToken('public');
    const res = await gridKpiAPI.getPublicKpis(gToken, userId, dashboardKey, password);
    if (res.error) {
      switch (res.code) {
        case 'EXPIRED':
          setReasonBlocker('linkExpired');
          closeModal();
          break;
        case 'DASH_NOT_FOUND':
          setReasonBlocker('notFound');
          closeModal();
          break;
        case 'PASSWORD_REQUIRED':
          setReasonBlocker('passwordProtected');
          handlePasswordModal(loadPage);
          break;
        case 'INVALID_PASSWORD': {
          setAlertConfig({
            type: 'error',
            text: 'Acesso não permitido!',
            child: 'Senha inválida. Tente novamente!',
          });
          setReasonBlocker('passwordProtected');
          break;
        }
        case 'CAPTCHA_FAILED':
          setAlertConfig({
            type: 'error',
            text: 'Acesso não permitido!',
            child: 'Foi detectado tráfego incomum em sua rede de computadores. Tente novamente mais tarde!',
          });
          setReasonBlocker('error');
          closeModal();
          break;
        default:
          setReasonBlocker('error');
          closeModal();
          break;
      }
    } else closeModal();
  };

  useEffect(() => {
    if (!isMounted.current) {
      if (!userId || !dashboardKey) return;
      loadPage();
    }
  }, [dashboardKey, gridKpiAPI, userId]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (gridSizeLocal === null) setGridSize(kpisLayout.size || 'auto');
    else setGridSize(gridSizeLocal);
  }, [kpisLayout?.size]);

  const handleAction = () => {
    switch (reasonBlocker) {
      case 'passwordProtected':
        handlePasswordModal(loadPage);
        break;
      case 'linkExpired':
        // TODO: ask for new link
        break;
      default:
        break;
    }
  };

  // TODO: modal to request guest access
  // const requestGuestAccess = () => {
  //   setModalConfig({
  //     title: 'Solicite acesso',
  //     children: (
  //       <></>
  //     ),
  //   });
  // };

  const closeInfo = () => setShowInfo(false);

  const getOriginalDashPath = () => {
    if (!dashboardDoc) return '';
    return `/home/${urlHash(`${`${dashboardDoc.owner}_${dashboardDoc.originalId}`}/${dashboardDoc.owner}`)}`;
  };

  const handleSize = () => {
    const changeSize = async (sz) => {
      setGridSize(sz);
      setGridSizeLocal(sz);
      closeModal();
    };

    setModalConfig({
      title: 'Largura do Grid',
      className: 'modal_grid_size',
      children: (
        <DashGridSize
          savedSize={gridSize}
          close={closeModal}
          submit={changeSize}
        />
      ),
    });
  };

  return (
    <>
      {(isLoading || !recaptcha.ready) && <Loader />}
      {(!isLoading && !dashboardDoc) ? (
        <PublicBlocker reason={reasonBlocker} action={handleAction} />
      ) : (
        <>
          <PublicHeader dashboardDoc={dashboardDoc} />
          {showInfo && (
            <InfoBannerContainer
              close={closeInfo}
              dashboardDoc={dashboardDoc}
              dashPath={getOriginalDashPath()}
            />
          )}
          <PublicKpiToolbar
            dashboardDoc={dashboardDoc}
            markers={markers}
            handleSize={handleSize}
          />
          <PublicGridKpi
            kpiItemList={kpiItemList}
            dashboardDoc={dashboardDoc}
            kpisLayout={kpisLayout}
            gridBreakpoint={gridBreakpoint}
            setGridBreakpoint={setGridBreakpoint}
            gridSize={gridSize}
          />
        </>
      )}
    </>
  );
};

export default PublicPage;
